import React from 'react';
import userDefinedFunctionHelper from '../../../infrastructure/helpers/functions/userDefinedFunctions/userDefinedFunctionHelper';
import referenceValidator from '../../../infrastructure/helpers/functions/validator/referenceValidator';
import reportConfigNavigation from '../../../infrastructure/helpers/report/navigation/reportConfigNavigation';
import ReportCollections from '../../../infrastructure/types/Functions/ReportCollections';
import Assignment from '../../../types/functions/Assignment/Assignment';
import locationType from '../../../types/functions/Location/LocationType';
import ReportLogicLocation from '../../../types/functions/Location/ReportLogicLocation';
import ReportConfig from '../../../types/report/ReportConfig';
import UserDefinedFunction from '../../../types/report/UserDefinedFunction';
import ErrorList from '../../Common/ErrorList/ErrorList';
import ReportLogicList from '../Common/ReportLogicList/ReportLogicList';
import { getColumns } from './UserDefinedFunctionBuilder.Grid';
import UserDefinedFunctionSelector from './UserDefinedFunctionSelector/UserDefinedFunctionSelector';
import WarningModal from '../../Common/Modals/WarningModal/WarningModal';
import { reportType } from '../../../infrastructure/constants/reportType';
import ReportComponentStatus from '../Common/ReportComponentStatus/ReportComponentStatus';
import ComponentType from '../../../types/report/ComponentType';
import appRoutes from '../../../infrastructure/constants/appRoutes';
import { onReferenceSelected, reportConfigPrefix } from '../../../infrastructure/helpers/report/reference/helper';
import SelectOption from '../../../infrastructure/types/SelectOption';
import copyObject from '../../../infrastructure/helpers/common/copyObject';
import reportLogicReferenceHelper from '../../../infrastructure/helpers/functions/report/reportLogicReferenceHelper';
import { htmlIds } from '../../../infrastructure/helpers/report/navigation/reportConfigNavigation.HtmlIds';
import Mode from '../../../types/report/Mode';

interface Props {
    isWaiting: boolean;
    isReadOnly: boolean;
    functionNumber: number | null;
    reportConfig: ReportConfig;
    collections: ReportCollections;
    onSelected: (number: number) => void;
    onChange: (dictionaries: UserDefinedFunction[]) => void;
    clipboard: ReportLogicLocation | null;
    onClipboardChange: (clipboard: ReportLogicLocation) => void;
}
interface State {
    showWarningModal: boolean;
}
class UserDefinedFunctionBuilder extends React.Component<Props, State> {

    constructor(props: Props) {
        super(props);

        this.state = {
            showWarningModal: false
        }

        this.onAddFunctionClick = this.onAddFunctionClick.bind(this);
        this.onRemoveFunctionClick = this.onRemoveFunctionClick.bind(this);
        this.onNameChange = this.onNameChange.bind(this);
        this.onReturnTypeChange = this.onReturnTypeChange.bind(this);
        this.onBusinessDescriptionChange = this.onBusinessDescriptionChange.bind(this);
        this.onFunctionChange = this.onFunctionChange.bind(this);
        this.getContent = this.getContent.bind(this);
        this.onRemoveFunctionApproved = this.onRemoveFunctionApproved.bind(this);
        this.onGlobalFunctionLinkClick = this.onGlobalFunctionLinkClick.bind(this);
        this.onGlobalFunctionReferenceSelected = this.onGlobalFunctionReferenceSelected.bind(this);
        this.getUdfReferences = this.getUdfReferences.bind(this);
        this.onGlobalUdfModeClick = this.onGlobalUdfModeClick.bind(this);
    }

    onAddFunctionClick(): void {

        let udf = userDefinedFunctionHelper.getDefault();

        if (this.props.reportConfig.type === reportType.accuracy) {

            let now = new Date(Date.now());
            udf.lastUpdatedTime = now.toISOString().slice(0, -5); // Remove '.fffZ'
        }

        userDefinedFunctionHelper.add(this.props.reportConfig.userDefinedFunctions, udf);

        this.props.onChange(this.props.reportConfig.userDefinedFunctions);
    }

    validateReferences(): string {
        return referenceValidator.validateUserDefinedFunctionReference(this.props.functionNumber as number, this.props.reportConfig) || '';
    }

    onRemoveFunctionClick(): void {

        let functionNumber = this.props.functionNumber || 0;

        let location = this.getLocation(functionNumber, []);

        let error = this.validateReferences();

        if (error) {
            let target = userDefinedFunctionHelper.get(this.props.reportConfig.userDefinedFunctions, location);

            target.errors = [error];

            this.props.onChange(this.props.reportConfig.userDefinedFunctions);

            return;
        }

        this.setState({ showWarningModal: true });
    }

    onRemoveFunctionApproved() {

        let location = this.getLocation(this.props.functionNumber as number, []);

        let reduced = this.props.reportConfig.userDefinedFunctions.filter(x => x.number !== location.statementNumber);

        this.props.onChange(reduced);

        this.setState({ showWarningModal: false });
    }

    onNameChange(name: string, number: number): void {

        let location = this.getLocation(number, []);

        this.onFunctionChange(location, { name: name } as UserDefinedFunction);
    }

    onReturnTypeChange(dataType: string, number: number, assignment: Assignment): void {

        let location = this.getLocation(number, []);

        //let proceed = window.confirm('Changing the data type will reset the logic for this field.');
        //if (!proceed) {
        //    return;
        //}

        this.onFunctionChange(location, { returnType: dataType } as UserDefinedFunction);
    }

    onBusinessDescriptionChange(businessDescription: string, number: number): void {

        let location = this.getLocation(number, []);

        this.onFunctionChange(location, { businessDescription: businessDescription } as UserDefinedFunction);
    }

    getLocation(number: number, argumentIndexes: number[]): ReportLogicLocation {

        let location = {} as ReportLogicLocation;

        location.statement = locationType.statement.userDefinedFunction;
        location.statementNumber = number;
        location.argumentIndexes = argumentIndexes;

        return location;
    }

    onFunctionChange(location: ReportLogicLocation, changes: UserDefinedFunction): void {

        let target = userDefinedFunctionHelper.get(this.props.reportConfig.userDefinedFunctions, location);

        Object.assign(target, changes);

        this.props.onChange(this.props.reportConfig.userDefinedFunctions);
    }

    getContent(func: UserDefinedFunction): JSX.Element {

        return (
            <>
                {
                    this.props.children
                }

                <ErrorList errors={func?.errors || []} className="mt-2" />

                <ReportComponentStatus
                    reportType={this.props.reportConfig.type}
                    lastUpdatedTime={func.lastUpdatedTime}
                    isReadOnly={this.props.isReadOnly} />
            </>
        );
    }

    onGlobalFunctionLinkClick(functionNumber: number, configId: number) {

        let route = `${appRoutes.reporting.edit}?id=${configId}&htmlid=${reportConfigNavigation.buildFunctionId(functionNumber)}&section=${htmlIds.userDefinedFunctions}` || '';

        window.open(route)?.focus();
    }

    onGlobalFunctionReferenceSelected(referencedFunctionNumber: number, value: string, text: string) {
        if (text.startsWith(reportConfigPrefix) && this.props.reportConfig.type === reportType.globalModule) {

            this.onGlobalFunctionLinkClick(referencedFunctionNumber, parseInt(value));
        }
        else if (text.startsWith(ComponentType.UserDefinedFunction)) {
            this.props.onSelected(parseInt(value));
        }
        else {
            onReferenceSelected(value);
        }
    }

    onGlobalUdfModeClick(globalFunctionId: number) {

        let functionNumber = this.props.collections.globalModuleConfig.userDefinedFunctions.find(x => x.id === globalFunctionId)?.number as number;

        this.onGlobalFunctionLinkClick(functionNumber, this.props.collections.globalModuleConfig.id);
    }

    getUdfReferences(number: number): SelectOption[] {

        let config = copyObject(this.props.reportConfig);

        config.userDefinedFunctions = config.userDefinedFunctions.filter(x => x.number !== number);

        let references = reportLogicReferenceHelper.getUserDefinedFunctionReferences(number, config, this.props.collections);

        if (this.props.reportConfig.type === reportType.globalModule) {

            let id = this.props.reportConfig.userDefinedFunctions.find(x => x.number === number)?.id as number;

            references = references.concat(this.props.collections.globalModuleReferences
                .filter(x => x.componentId === id && x.componentType === ComponentType.UserDefinedFunction.replace(/\s/g, ''))
                .map((x) => { return { number: x.componentNumber, value: x.reportConfigId.toString(), text: `Config: ${x.name}` } as SelectOption }) as SelectOption[]);
        }

        return references;
    }

    render(): JSX.Element {

        return (
            <>
                <div className="col mt-2">
                    <UserDefinedFunctionSelector
                        isWaiting={this.props.isWaiting}
                        functionNumber={this.props.functionNumber}
                        functions={this.props.reportConfig.userDefinedFunctions}
                        onFunctionSelected={this.props.onSelected} />
                </div>
                {
                    this.props.functionNumber &&
                    <>
                        <ReportLogicList
                            isWaiting={this.props.isWaiting}
                            items={this.props.reportConfig.userDefinedFunctions.filter(x => x.number === this.props.functionNumber)}
                            columns={getColumns(this.onNameChange, this.onReturnTypeChange, this.onBusinessDescriptionChange, this.onRemoveFunctionClick,
                                this.props.isReadOnly, this.onGlobalUdfModeClick, this.onGlobalFunctionReferenceSelected, this.getUdfReferences)}
                            getContent={this.getContent}
                            getKey={a => a.number}
                            createHtmlId={v => reportConfigNavigation.buildFunctionId(v.number)}
                            children />
                    </>
                }
                {this.state.showWarningModal &&
                    <WarningModal
                        onOkClick={this.onRemoveFunctionApproved}
                        onCancelClick={() => this.setState({ showWarningModal: false })}
                        title='Remove item'
                        message='Are you sure you want to delete this item? This action cannot be undone.' />
                }
                <div className="row no-gutters mb-3">
                    <div className="mb-4">
                        <button
                            onClick={this.onAddFunctionClick}
                            className="btn btn-light">
                            + Function
                        </button>
                    </div>
                </div>
            </>
        );
    }
}

export default UserDefinedFunctionBuilder;
